import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";

const STORAGE_KEY = "progres_attribution_v1";
const VISITOR_KEY = "progres_visitor_id";

function uuid4() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "vid-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function readAttribution() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeAttribution(attr) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(attr));
  } catch {}
}

function visitorId() {
  let v = null;
  try {
    v = localStorage.getItem(VISITOR_KEY);
  } catch {}
  if (!v) {
    v = uuid4();
    try {
      localStorage.setItem(VISITOR_KEY, v);
    } catch {}
  }
  return v;
}

export function getAttribution() {
  return readAttribution();
}

/**
 * On every route change:
 * - if URL contains utm_* / ref / gclid — save as attribution (first-touch, we keep the FIRST one)
 * - fire POST /api/track/visit (anonymous, no auth required)
 */
export function useVisitTracker() {
  const location = useLocation();
  const lastPath = useRef(null);

  useEffect(() => {
    if (lastPath.current === location.pathname + location.search) return;
    lastPath.current = location.pathname + location.search;

    const params = new URLSearchParams(location.search);
    const captured = {
      utm_source: params.get("utm_source") || params.get("ref") || params.get("gclid") ? "google" : params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
      utm_content: params.get("utm_content"),
      utm_term: params.get("utm_term"),
    };
    // Only save if something present
    const hasAny = Object.values(captured).some(Boolean) || params.get("ref");
    let attribution = readAttribution();
    if (hasAny && !attribution) {
      attribution = {
        utm_source: captured.utm_source || params.get("ref") || null,
        utm_medium: captured.utm_medium || null,
        utm_campaign: captured.utm_campaign || null,
        utm_content: captured.utm_content || null,
        utm_term: captured.utm_term || null,
        referrer: typeof document !== "undefined" ? document.referrer || null : null,
        first_seen: new Date().toISOString(),
      };
      writeAttribution(attribution);
    }

    const payload = {
      path: location.pathname,
      visitor_id: visitorId(),
      utm_source: captured.utm_source || attribution?.utm_source || null,
      utm_medium: captured.utm_medium || attribution?.utm_medium || null,
      utm_campaign: captured.utm_campaign || attribution?.utm_campaign || null,
      utm_content: captured.utm_content || attribution?.utm_content || null,
      utm_term: captured.utm_term || attribution?.utm_term || null,
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
    };
    api.post("/track/visit", payload).catch(() => {});
  }, [location.pathname, location.search]);
}
